/**
 * Currency detection and formatting utilities
 * Detects currency based on user's browser locale
 */

export interface CurrencyInfo {
    code: string; // ISO currency code (USD, EUR, GBP, etc.)
    symbol: string; // Currency symbol ($, €, £, etc.)
    locale: string; // Locale string (en-US, en-GB, etc.)
}

/**
 * Detects user's currency based on browser locale
 * Falls back to USD if detection fails
 */
export function detectCurrency(): CurrencyInfo {
    try {
        // Get browser locale
        const locale = navigator.language || navigator.languages?.[0] || 'en-US';

        // Map common locales to currencies
        const localeToCurrency: Record<string, { code: string; symbol: string }> = {
            'en-US': { code: 'USD', symbol: '$' },
            'en-CA': { code: 'CAD', symbol: 'C$' },
            'en-GB': { code: 'GBP', symbol: '£' },
            'en-AU': { code: 'AUD', symbol: 'A$' },
            'en-NZ': { code: 'NZD', symbol: 'NZ$' },
            'de-DE': { code: 'EUR', symbol: '€' },
            'de-AT': { code: 'EUR', symbol: '€' },
            'fr-FR': { code: 'EUR', symbol: '€' },
            'fr-CA': { code: 'CAD', symbol: 'C$' },
            'es-ES': { code: 'EUR', symbol: '€' },
            'es-MX': { code: 'MXN', symbol: 'MX$' },
            'it-IT': { code: 'EUR', symbol: '€' },
            'pt-BR': { code: 'BRL', symbol: 'R$' },
            'pt-PT': { code: 'EUR', symbol: '€' },
            'nl-NL': { code: 'EUR', symbol: '€' },
            'pl-PL': { code: 'PLN', symbol: 'zł' },
            'ru-RU': { code: 'RUB', symbol: '₽' },
            'ja-JP': { code: 'JPY', symbol: '¥' },
            'ko-KR': { code: 'KRW', symbol: '₩' },
            'zh-CN': { code: 'CNY', symbol: '¥' },
            'zh-TW': { code: 'TWD', symbol: 'NT$' },
            'hi-IN': { code: 'INR', symbol: '₹' },
            'ar-SA': { code: 'SAR', symbol: '﷼' },
            'tr-TR': { code: 'TRY', symbol: '₺' },
        };

        // Try exact match first
        if (localeToCurrency[locale]) {
            return {
                code: localeToCurrency[locale].code,
                symbol: localeToCurrency[locale].symbol,
                locale,
            };
        }

        // Try language code match (e.g., 'en' for 'en-IN')
        const languageCode = locale.split('-')[0];
        const matchingLocale = Object.keys(localeToCurrency).find(
            (key) => key.startsWith(languageCode + '-')
        );

        if (matchingLocale) {
            return {
                code: localeToCurrency[matchingLocale].code,
                symbol: localeToCurrency[matchingLocale].symbol,
                locale,
            };
        }

        // Try using Intl API to get currency
        try {
            // const formatter = new Intl.NumberFormat(locale, {
            //     style: 'currency',
            //     currency: 'USD', // Default to USD
            // });

            // Extract currency code from locale if possible
            const region = locale.split('-')[1];
            if (region) {
                // Try to get currency for the region
                const currencyMap: Record<string, string> = {
                    'US': 'USD',
                    'GB': 'GBP',
                    'CA': 'CAD',
                    'AU': 'AUD',
                    'DE': 'EUR',
                    'FR': 'EUR',
                    'ES': 'EUR',
                    'IT': 'EUR',
                    'BR': 'BRL',
                    'MX': 'MXN',
                    'JP': 'JPY',
                    'KR': 'KRW',
                    'CN': 'CNY',
                    'IN': 'INR',
                };

                const currencyCode = currencyMap[region] || 'USD';
                const symbol = getCurrencySymbol(currencyCode, locale);

                return {
                    code: currencyCode,
                    symbol,
                    locale,
                };
            }
        } catch {
            // Fall through to default
        }

        // Default to USD
        return {
            code: 'USD',
            symbol: '$',
            locale: 'en-US',
        };
    } catch (error) {
        console.warn('Currency detection failed, defaulting to USD:', error);
        return {
            code: 'USD',
            symbol: '$',
            locale: 'en-US',
        };
    }
}

/**
 * Gets currency symbol for a given currency code
 */
function getCurrencySymbol(currencyCode: string, locale: string): string {
    try {
        const formatter = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });

        // Extract symbol from formatted string
        const parts = formatter.formatToParts(1);
        const symbolPart = parts.find((part) => part.type === 'currency');
        return symbolPart?.value || '$';
    } catch {
        // Fallback symbols
        const fallbackSymbols: Record<string, string> = {
            'USD': '$',
            'EUR': '€',
            'GBP': '£',
            'JPY': '¥',
            'CNY': '¥',
            'INR': '₹',
            'CAD': 'C$',
            'AUD': 'A$',
            'BRL': 'R$',
            'MXN': 'MX$',
            'KRW': '₩',
            'RUB': '₽',
            'TRY': '₺',
        };
        return fallbackSymbols[currencyCode] || '$';
    }
}

/**
 * Formats a number as currency
 */
export function formatCurrency(amount: number, currencyInfo?: CurrencyInfo): string {
    const currency = currencyInfo || detectCurrency();

    try {
        const formatter = new Intl.NumberFormat(currency.locale, {
            style: 'currency',
            currency: currency.code,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        });
        return formatter.format(amount);
    } catch {
        // Fallback formatting
        return `${currency.symbol}${amount.toLocaleString()}`;
    }
}

/**
 * Gets stored currency preference or detects new one
 */
export function getCurrencyPreference(): CurrencyInfo {
    try {
        const stored = localStorage.getItem('flashusdt:currency_preference');
        if (stored) {
            return JSON.parse(stored);
        }
    } catch {
        // Ignore parse errors
    }

    const detected = detectCurrency();
    // Store for future use
    try {
        localStorage.setItem('flashusdt:currency_preference', JSON.stringify(detected));
    } catch {
        // Ignore storage errors (private mode, etc.)
    }

    return detected;
}

